import { useEffect, useState } from 'react';
import { useAuth, api } from '../contexts/AuthContext';
import { CheckSquare, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import TaskDetailSheet from '../components/TaskDetailSheet';

const statusConfig = {
  to_be_done: { label: 'To Be Done', bg: '#E2E8F0', text: '#34495e', icon: CheckSquare },
  doing: { label: 'Doing', bg: '#f39c12', text: '#FFFFFF', icon: Clock },
  need_clarification: { label: 'Need Clarification', bg: '#e74c3c', text: '#FFFFFF', icon: AlertCircle },
  done: { label: 'Done', bg: '#27ae60', text: '#FFFFFF', icon: CheckCircle },
};

export default function DashboardPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState(null);

  const fetchData = async () => {
    try {
      const [tasksRes, usersRes, catsRes] = await Promise.all([
        api.get('/tasks'),
        api.get('/users'),
        api.get('/categories'),
      ]);
      setTasks(tasksRes.data.tasks);
      setUsers(usersRes.data.users);
      setCategories(catsRes.data.categories);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const selectedTask = selectedTaskId ? tasks.find(t => t.task_id === selectedTaskId) || null : null;

  const isAdmin = user?.role === 'admin';

  // Members see only their assigned tasks; admins see all
  const displayTasks = isAdmin ? tasks : tasks.filter(t => t.assigned_to === user?.user_id);

  const stats = Object.entries(statusConfig).map(([key, config]) => ({
    ...config,
    key,
    count: displayTasks.filter(t => t.status === key).length,
  }));

  const recentTasks = displayTasks.slice(0, 10);
  const getUserName = (task) => {
    if (task?.assigned_to_removed_name) return task.assigned_to_removed_name;
    if (!task?.assigned_to) return 'Unassigned';
    return users.find(u => u.user_id === task.assigned_to)?.name || 'Unassigned';
  };
  const getCategoryName = (catId) => categories.find(c => c.category_id === catId)?.name || '-';

  return (
    <div data-testid="dashboard-page">
      <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-[#34495e] mb-1">
        Dashboard
      </h2>
      <p className="text-sm text-[#7F8C8D] mb-6 leading-relaxed">
        Welcome back, {user?.name || user?.email}{!isAdmin && ' — showing your assigned tasks'}
      </p>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="stats-grid">
        {stats.map(s => (
          <div
            key={s.key}
            className="bg-white border border-[#E2E8F0] rounded-sm p-4 flex items-center gap-4 hover:scale-[1.02] transition-transform"
            data-testid={`stat-${s.key}`}
          >
            <div
              className="w-10 h-10 rounded-sm flex items-center justify-center"
              style={{ backgroundColor: s.bg, color: s.text }}
            >
              <s.icon size={20} />
            </div>
            <div>
              <p className="text-2xl font-black text-[#34495e]">{s.count}</p>
              <p className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Tasks */}
      <div className="bg-white border border-[#E2E8F0] rounded-sm">
        <div className="p-4 border-b border-[#E2E8F0]">
          <h3 className="text-lg font-black tracking-tight text-[#34495e]">{isAdmin ? 'Recent Tasks' : 'My Recent Tasks'}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="recent-tasks-table">
            <thead>
              <tr className="bg-[#F8F9FA]">
                <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Task</th>
                <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Category</th>
                <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Assigned To</th>
                <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Status</th>
                <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Est. Completion</th>
              </tr>
            </thead>
            <tbody>
              {recentTasks.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-[#BDC3C7] text-sm">
                    No tasks yet. Create your first task!
                  </td>
                </tr>
              ) : (
                recentTasks.map(task => {
                  const sc = statusConfig[task.status] || statusConfig.to_be_done;
                  const overdue = task.status !== 'done' && task.estimated_completion && new Date() > new Date(task.estimated_completion);
                  return (
                    <tr
                      key={task.task_id}
                      onClick={() => setSelectedTaskId(task.task_id)}
                      className={`border-b border-[#E2E8F0] last:border-0 cursor-pointer transition-colors ${overdue ? 'bg-red-50 hover:bg-red-100/70' : 'hover:bg-[#F8F9FA]'}`}
                      data-testid={`dashboard-recent-task-${task.task_id}`}
                    >
                      <td className={`px-4 py-3 text-sm ${overdue ? 'text-[#e74c3c] font-bold' : 'text-[#34495e]'}`}>
                        {overdue && <span className="inline-block w-2 h-2 rounded-full bg-[#e74c3c] mr-1.5 -mt-0.5" />}
                        {task.title}
                      </td>
                      <td className="px-4 py-3 text-sm text-[#7F8C8D]">{getCategoryName(task.category_id)}</td>
                      <td className="px-4 py-3 text-sm text-[#7F8C8D]">
                        {task.assigned_to_removed_name ? (
                          <span className="text-[#e74c3c] italic text-xs">{task.assigned_to_removed_name}</span>
                        ) : getUserName(task)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="inline-block px-2 py-0.5 rounded-sm text-xs font-bold"
                          style={{ backgroundColor: sc.bg, color: sc.text }}
                        >
                          {sc.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-[#7F8C8D]">
                        {task.estimated_completion
                          ? format(new Date(task.estimated_completion), 'MMM d, yyyy')
                          : '-'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <TaskDetailSheet
        task={selectedTask}
        open={!!selectedTask}
        onClose={() => setSelectedTaskId(null)}
        users={users}
        categories={categories}
        onTaskUpdated={() => fetchData()}
      />
    </div>
  );
}
